import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, Calendar, ArrowLeft, Handshake } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Spinner, DemoBadge } from "@/components/common";
import { ReportDialog } from "@/components/ReportDialog";
import { timeAgo } from "@/pages/Opportunities";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

export default function OpportunityDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const authed = user && typeof user === "object";
  const [opp, setOpp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/opportunities/${id}`).then(({ data }) => setOpp(data)).catch(() => setOpp(false)).finally(() => setLoading(false));
  }, [id]);

  const express = async () => {
    if (!authed) { navigate("/login"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/opportunities/${id}/interest`, { message: message.trim() || undefined });
      toast.success(data.message);
      setOpen(false);
      setMessage("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="container-nx section-nx"><Spinner /></div>;
  if (!opp) return (
    <div className="container-nx section-nx text-center">
      <p className="text-slate-500">Opportunity not found.</p>
      <Link to="/opportunities" className="btn-outline-navy mt-4 inline-flex">Back to opportunities</Link>
    </div>
  );

  const isAuthor = authed && user.id === opp.author_id;

  return (
    <div className="container-nx section-nx max-w-3xl">
      <Link to="/opportunities" className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0B192C]" data-testid="back-to-opportunities">
        <ArrowLeft className="h-4 w-4" /> Back to opportunities
      </Link>

      <div className="card-nx p-6 sm:p-8">
        <div className="flex items-start justify-between gap-3">
          <span className="chip-gold">{opp.category}</span>
          {opp.is_demo && <DemoBadge />}
        </div>
        <h1 className="mt-4 text-2xl font-bold text-[#0B192C] font-head">{opp.title}</h1>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
          <span>By {opp.author_name}</span>
          {opp.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{opp.location}</span>}
          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{timeAgo(opp.created_at)}</span>
        </div>

        <p className="mt-6 whitespace-pre-wrap text-sm leading-relaxed text-slate-600">{opp.description}</p>

        {!isAuthor && (
          <button onClick={() => (authed ? setOpen(true) : navigate("/login"))} className="btn-gold mt-8" data-testid="express-interest-btn">
            <Handshake className="h-4 w-4" /> Express interest
          </button>
        )}

        <div className="mt-6 border-t border-slate-100 pt-4">
          <ReportDialog targetType="opportunity" targetId={opp.id} label="Report this post" />
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Express interest</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">The author will be notified. Add an optional message.</p>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="I'm interested because..." className="min-h-[100px]" data-testid="interest-message-input" />
          <DialogFooter>
            <button onClick={express} disabled={sending} className="btn-gold" data-testid="interest-send-btn">
              {sending ? "Sending..." : "Send interest"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
